import { Bot, Server } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { PlatformAssistant, PlatformProject } from "@/lib/platformTypes";
import { cn } from "@/lib/utils";
import { EmptyState, ViewSection } from "@/views/shared";
import { HomeRow, HomeRowList, HomeRowsSkeleton, ViewAllAction } from "./HomeRow";

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const healthy = status === "active" || status === "deployed" || status === "ready";
  return (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0 px-2 py-0 text-[10px] font-medium",
        healthy ? "border-primary/40 text-primary" : "border-border text-muted-foreground",
      )}
    >
      {status}
    </Badge>
  );
}

export function AssistantShortcuts({
  assistants,
  loaded,
  error,
  onOpen,
  onViewAll,
}: {
  assistants: PlatformAssistant[];
  loaded: boolean;
  error?: string;
  onOpen: (id: string) => void;
  onViewAll: () => void;
}) {
  return (
    <ViewSection title="Assistants" action={<ViewAllAction onClick={onViewAll} />}>
      {!loaded ? (
        <HomeRowsSkeleton rows={2} />
      ) : error ? (
        <EmptyState message={error} />
      ) : assistants.length === 0 ? (
        <EmptyState message="No assistants deployed." />
      ) : (
        <HomeRowList>
          {assistants.map((a) => (
            <HomeRow key={a.id} onClick={() => onOpen(a.id)} className="py-2.5">
              <Bot className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="truncate text-sm text-foreground">{a.name}</span>
                {a.model && (
                  <span className="truncate font-mono text-[10px] text-muted-foreground">
                    {a.model}
                  </span>
                )}
              </div>
              <StatusBadge status={a.status} />
            </HomeRow>
          ))}
        </HomeRowList>
      )}
    </ViewSection>
  );
}

export function ServerShortcuts({
  servers,
  loaded,
  error,
  onOpen,
  onViewAll,
}: {
  servers: PlatformProject[];
  loaded: boolean;
  error?: string;
  onOpen: (id: string) => void;
  onViewAll: () => void;
}) {
  return (
    <ViewSection title="MCP servers" action={<ViewAllAction onClick={onViewAll} />}>
      {!loaded ? (
        <HomeRowsSkeleton rows={2} />
      ) : error ? (
        <EmptyState message={error} />
      ) : servers.length === 0 ? (
        <EmptyState message="No MCP servers deployed." />
      ) : (
        <HomeRowList>
          {servers.map((s) => (
            <HomeRow key={s.id} onClick={() => onOpen(s.id)} className="py-2.5">
              <Server className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{s.name}</span>
              <StatusBadge status={s.status} />
            </HomeRow>
          ))}
        </HomeRowList>
      )}
    </ViewSection>
  );
}
